import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import { gdprService, geoService, profileService, parentalControlService } from '@bedrock/fido-sdk';
import generateDeviceIdUtils, { isValidDeviceId, fixDeviceId } from '@bedrock-rdk/utils/src/deviceId';
import { getUserState, getDeviceId, getHomeId } from './user.selectors';
import { getUserApiFeature } from '../features/features.selectors';
import { getFormattedConsents } from './user.utils';
import { bootstrapEngines } from '../analytics/analytics.actions';
import { errorAction } from '../error/error.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { showPopupAction } from '../popup/popup.actions';
import { MAX_PARENTAL_CODE_POPUP_ID } from '../../modules/popups/popups.constants';
import { trackGDPRConsent } from '../analytics/analytics.utils';

export const STORAGE_WRITE_FAILURE = 'user/STORAGE_WRITE_FAILURE';
export const STORAGE_SUPPORT_FAILURE = 'user/STORAGE_SUPPORT_FAILURE';

export const RESTORE_DEVICE_DATA = 'user/RESTORE_DEVICE_DATA';
export const RESET_DEVICE_DATA = 'user/RESET_DEVICE_DATA';
export const SET_DO_NOT_TRACK = 'user/SET_DO_NOT_TRACK';
export const SET_USER_CONSENTS = 'user/SET_USER_CONSENTS';
export const GENERATE_DEVICE_ID = 'user/GENERATE_DEVICE_ID';
export const UPDATE_DEVICE_ID = 'user/UPDATE_DEVICE_ID';
export const SYNCHRONIZE_USER_CONSENTS = 'user/SYNCHRONIZE_USER_CONSENTS';

// User
export const CALL_USER_CONSENTS_SUCCESS = 'user/CALL_USER_CONSENTS_SUCCESS';
export const CALL_USER_CONSENTS_FAILURE = 'user/CALL_USER_CONSENTS_FAILURE';

export const GET_USER_CONSENTS_SUCCESS = 'user/GET_USER_CONSENTS_SUCCESS';
export const GET_USER_CONSENTS_FAILURE = 'user/GET_USER_CONSENTS_FAILURE';

export const SET_USER_UID = 'user/SET_USER_UID';

export const USER_FETCH_LAST_CLIPS_SUCCESS = 'user/FETCH_LAST_CLIPS_SUCCESS';
export const USER_FETCH_LAST_CLIPS_FAILURE = 'user/FETCH_LAST_CLIPS_FAILURE';

export const USER_RESTORE_PROFILE_UID = 'user/RESTORE_PROFILE_UID';

export const PARING_SET_PREVIOUS_PROFILE_UID = 'user/PARING_SET_PREVIOUS_PROFILE_UID';

// Geo info
export const CALL_GEO_SUCCESS = 'app/CALL_GEO_SUCCESS';
export const CALL_GEO_FAILURE = 'app/CALL_GEO_FAILURE';

export const FETCH_PROFILE_INFOS_SUCCESS = 'user/FETCH_PROFILE_INFOS_SUCCESS';
export const FETCH_PROFILE_INFOS_FAILURE = 'user/FETCH_PROFILE_INFOS_FAILURE';

// Parental code
export const PARENTAL_LOCK_STATUS = 'user/PARENTAL_LOCK_STATUS';

// Account completion (SSI flow)
export const SET_ACCOUNT_COMPLETE = 'user/SET_ACCOUNT_COMPLETE';

export const storageWriteFailure = error => ({ type: STORAGE_WRITE_FAILURE, error });
export const storageSupportFailure = error => ({ type: STORAGE_SUPPORT_FAILURE, error });

export const setPreviousProfileUID = previousProfileUid => ({
  type: PARING_SET_PREVIOUS_PROFILE_UID,
  previousProfileUid,
});

export const generateDeviceId = () => ({
  type: GENERATE_DEVICE_ID,
  deviceId: generateDeviceIdUtils(),
});

export const fetchProfileInfosSuccess = payload => ({
  type: FETCH_PROFILE_INFOS_SUCCESS,
  ...payload,
});

export const getUserConsentsSuccess = () => ({ type: GET_USER_CONSENTS_SUCCESS });
export const getUserConsentsFailure = error => ({ type: GET_USER_CONSENTS_FAILURE, error: error.message });
export const completeAccount = () => ({ type: SET_ACCOUNT_COMPLETE });

export const fetchProfileInfos = profileUid => async (dispatch, getState) => {
  const {
    pairing: { uid },
  } = getState();

  try {
    const profileInfos = await profileService.getProfile({ profileUid, userUid: uid });
    const {
      username,
      avatar: { imageExternalKey },
      is_kid: isKidsProfile,
    } = profileInfos;

    dispatch(
      fetchProfileInfosSuccess({
        profileAvatarId: imageExternalKey,
        username,
        isKidsProfile,
        profileUid,
      }),
    );
  } catch (error) {
    dispatch({ type: FETCH_PROFILE_INFOS_FAILURE });
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
  }
};

export const restoreDeviceData = () => ({
  type: RESTORE_DEVICE_DATA,
});

export const updateDeviceId = deviceId => ({
  type: UPDATE_DEVICE_ID,
  deviceId,
});

export const setLocalUserConsents = consents => ({
  type: SET_USER_CONSENTS,
  ...consents,
});

export const migrateDeviceId = deviceId => dispatch => {
  if (!deviceId || isValidDeviceId(deviceId)) {
    return;
  }

  const updatedDeviceId = fixDeviceId(deviceId);
  dispatch(updateDeviceId(updatedDeviceId));
};

export const setUserConsentsOnApi = consents => async (dispatch, getState) => {
  const { cookies } = consents;
  const state = getState();
  const deviceId = getDeviceId(state);
  const homeId = getHomeId(state);
  const formattedConsents = getFormattedConsents(cookies);

  try {
    await gdprService.setUserConsents({ uid: homeId || deviceId, consents: formattedConsents });
    dispatch({ type: CALL_USER_CONSENTS_SUCCESS });
  } catch (error) {
    dispatch({ type: CALL_USER_CONSENTS_FAILURE, error: error.message });
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
  }
};

export const setRemoteConsents = consents => async (dispatch, getState) => {
  const state = getState();
  const { deviceId, synchronize, areUserPreferencesSet, cookies } = getUserState(state);
  const userApiFeature = getUserApiFeature(state);

  const currentConsents = {
    ...consents,
    ...cookies,
  };

  if (_isEqual(currentConsents, consents)) {
    return;
  }

  if (synchronize === null && !areUserPreferencesSet) {
    try {
      const remoteConsents = await gdprService.getUserConsents({ uid: deviceId });
      dispatch(getUserConsentsSuccess());

      if (Object.keys(remoteConsents).length !== 0) {
        dispatch(generateDeviceId());
        dispatch(setRemoteConsents(consents));

        return;
      }
    } catch (error) {
      dispatch(getUserConsentsFailure(error));
      dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
    }
  }

  if (userApiFeature) {
    dispatch(setUserConsentsOnApi({ cookies: consents }));
  }
};

export const setUserConsents = consents => dispatch => {
  trackGDPRConsent(consents);
  dispatch(setRemoteConsents(consents));
  dispatch(setLocalUserConsents(consents));
  dispatch(bootstrapEngines());
};

export const setGeoInfo = () => async dispatch => {
  try {
    const geoInfo = await geoService.fetch();
    dispatch({ type: CALL_GEO_SUCCESS, data: geoInfo });
  } catch (e) {
    dispatch({ type: CALL_GEO_FAILURE });
  }
};

export const setUserUid = uid => dispatch => dispatch({ type: SET_USER_UID, uid });

export const resetParentalLock = () => dispatch => dispatch({ type: PARENTAL_LOCK_STATUS, isUnlocked: null });

export const closeParentalLock = () => dispatch => dispatch({ type: PARENTAL_LOCK_STATUS, isUnlocked: false });

export const openParentalLock = () => dispatch => dispatch({ type: PARENTAL_LOCK_STATUS, isUnlocked: true });

export const fetchLastViewedClipsSuccess = lastViewdClips => dispatch => {
  try {
    dispatch({ type: USER_FETCH_LAST_CLIPS_SUCCESS, lastClips: lastViewdClips });
  } catch (error) {
    dispatch({ type: USER_FETCH_LAST_CLIPS_FAILURE, error });
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
  }
};

export const checkParentalRestrictionCode = code => async (dispatch, getState) => {
  const {
    user: { uid },
  } = getState();

  try {
    dispatch(resetParentalLock());
    await parentalControlService.checkParentalRestrictionCode({ uid, code });
    dispatch(openParentalLock());
  } catch (error) {
    const errorStatus = _get(error, 'status');
    const errorMessage = _get(error, 'message', '');
    if (errorStatus && errorStatus === 400) {
      dispatch(closeParentalLock());
    } else if (errorStatus && errorStatus === 403) {
      dispatch(showPopupAction(MAX_PARENTAL_CODE_POPUP_ID, {}));
    } else {
      dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.GENERAL, errorMessage));
    }
  }
};
